/* Slice 2G · Correction Pass 2 · Micro Pass — shared helpers for the
 * four customer-facing send controls.
 *
 * Kept intentionally tiny and side-effect free:
 *   - newRequestKey()      — one stable idempotency key per user
 *                            send action; reused if the user's own
 *                            handler re-invokes the request during
 *                            the same click. A NEW key is generated
 *                            by calling this again for an intentional
 *                            resend.
 *   - localizeSendError()  — maps the backend's server-owned recipient
 *                            override 400 string to a localized visible
 *                            wording via the caller's bound `t()`. All
 *                            other 400/500 detail strings are passed
 *                            through as-is.
 */

// The stable phrase returned by _resolve_recipient in the backend when
// a frontend-supplied to_email doesn't match the server-owned address.
// We intentionally do NOT parse it apart — we just detect it exactly
// and swap for the tenant's UI-language wording. Any other detail
// value (missing email, invalid email, provider category, etc.) is
// surfaced verbatim by the caller's existing fallback path.
const RECIPIENT_OVERRIDE_MARKER =
  "Recipient cannot be overridden";

/** Generate a fresh idempotency key. Returns a UUID v4 when the
 *  browser exposes `crypto.randomUUID`, otherwise a namespace-shaped
 *  fallback that is still statistically unique for a single-tab
 *  send action. */
export const newRequestKey = () => {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return `jc-${crypto.randomUUID()}`;
    }
  } catch (_) {
    /* fall through to Math.random fallback */
  }
  return `jc-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
};

/** Map the axios-shaped error to a visible, localized string.
 *  `t` is the caller's bound translator (`useLang().t`).
 *  `fallbackKey` is the caller's existing generic-failure translation
 *  key (e.g. `"proposals_form.toast_email_failed"`). */
export const localizeSendError = (err, t, fallbackKey) => {
  const detail = err && err.response && err.response.data && err.response.data.detail;
  if (typeof detail === "string" && detail.indexOf(RECIPIENT_OVERRIDE_MARKER) !== -1) {
    return t("email_common.recipient_override_error");
  }
  return detail || t(fallbackKey);
};
